import { LoginOptions, User } from './types';

type LoginFn = (loginOptions?: LoginOptions) => void;
type LogoutFn = () => void;
type GetAccessTokenFn = (ignoreCache?: boolean) => Promise<string>;
type Listener = (
  state: [isAuthenticated: boolean, isAuthenticating: boolean, user: User | null, error: Error | null]
) => void;

class AuthenticationClient {
  private static instance: AuthenticationClient;

  private loginFn: LoginFn;
  private logoutFn: LogoutFn;
  private getAccessTokenFn: GetAccessTokenFn;

  private listeners: Listener[] = [];

  private authenticated: boolean = false;
  private authenticating: boolean = false;
  private user: User | null = null;
  private error: Error | null = null;

  private constructor() {}

  public isAuthenticated(): boolean {
    return this.authenticated;
  }

  public getUser(): User | null {
    return this.user;
  }

  public subscribe(listener: Listener): void {
    this.listeners.push(listener);
    listener([this.authenticated, this.authenticating, this.user, this.error]);
  }

  public unsubscribe(listener: Listener): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  public update(isAuthenticated: boolean, isAuthenticating: boolean, user: User | null, error: Error | null) {
    this.authenticated = isAuthenticated;
    this.authenticating = isAuthenticating;
    this.user = user;
    this.error = error;

    this.listeners.forEach(listener => {
      listener([isAuthenticated, isAuthenticating, user, error]);
    });
  }

  public setFns(loginFn: LoginFn, logoutFn: LogoutFn, getAccessTokenFn: GetAccessTokenFn) {
    this.loginFn = loginFn;
    this.logoutFn = logoutFn;
    this.getAccessTokenFn = getAccessTokenFn;
  }
  public login(loginOptions?: LoginOptions): void {
    if (!this.loginFn) {
      throw new Error('Authentication functions not setup yet : missing login');
    }

    return this.loginFn(loginOptions);
  }

  public logout(): void {
    if (!this.logoutFn) {
      throw new Error('Authentication functions not setup yet : missing logout');
    }
    return this.logoutFn();
  }

  public getAccessToken(ignoreCache?: boolean): Promise<string> {
    if (!this.getAccessTokenFn) {
      return Promise.reject('Authentication functions not setup yet : missing getAccessToken');
    }
    return this.getAccessTokenFn(ignoreCache);
  }

  public getError(): Error | null {
    return this.error;
  }

  public static getInstance(): AuthenticationClient {
    if (!AuthenticationClient.instance) {
      AuthenticationClient.instance = new AuthenticationClient();
    }
    return AuthenticationClient.instance;
  }

  public static reset(): void {
    AuthenticationClient.instance = undefined;
  }
}

export { AuthenticationClient };
